import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Bell, Building2, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useEnterpriseContext } from '@/hooks/useEnterpriseContext';
import {
  getEnterpriseSettings,
  getEnterpriseTenant,
  updateEnterpriseSettings,
  updateEnterpriseTenant,
} from '@/services/api.client';
import type { EnterpriseSettings } from '@/services/api.client';

// ─── Details schema ──────────────────────────────────────────────────────────

const detailsSchema = z.object({
  companyName: z.string().min(1, 'Company name is required'),
  receptionHours: z.string().optional(),
});

type DetailsValues = z.infer<typeof detailsSchema>;

function DetailRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value ?? '—'}</span>
    </div>
  );
}

// ─── Reception toggle row ────────────────────────────────────────────────────

function ToggleRow({
  title,
  description,
  checked,
  onChange,
  disabled,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function EnterpriseAccountPage() {
  const ctx = useEnterpriseContext();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const tenantQuery = useQuery({
    queryKey: ['enterprise-tenant', ctx?.propertyId, ctx?.tenantId],
    queryFn: () => (ctx ? getEnterpriseTenant(ctx.propertyId, ctx.tenantId) : null),
    enabled: !!ctx,
  });

  const settingsQuery = useQuery({
    queryKey: ['enterprise-settings', ctx?.propertyId, ctx?.tenantId],
    queryFn: () => (ctx ? getEnterpriseSettings(ctx.propertyId, ctx.tenantId) : null),
    enabled: !!ctx,
  });

  const tenant = tenantQuery.data;
  const isLoading = tenantQuery.isLoading;
  const isError = tenantQuery.isError;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<DetailsValues>({
    resolver: zodResolver(detailsSchema),
    ...(tenant && {
      values: {
        companyName: tenant.companyName ?? '',
        receptionHours: tenant.receptionHours ?? '',
      },
    }),
  });

  const saveDetails = useMutation({
    mutationFn: (values: DetailsValues) =>
      ctx
        ? updateEnterpriseTenant(ctx.propertyId, ctx.tenantId, {
            companyName: values.companyName,
            ...(values.receptionHours && { receptionHours: values.receptionHours }),
          })
        : Promise.reject(new Error('Missing enterprise context')),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['enterprise-tenant', ctx?.propertyId, ctx?.tenantId],
      });
      setSaveError(null);
      setEditing(false);
    },
    onError: () => setSaveError('Failed to save. Please try again.'),
  });

  const saveSettings = useMutation({
    mutationFn: (next: Partial<EnterpriseSettings>) =>
      ctx
        ? updateEnterpriseSettings(ctx.propertyId, ctx.tenantId, next)
        : Promise.reject(new Error('Missing enterprise context')),
    onSuccess: (data) => {
      qc.setQueryData(['enterprise-settings', ctx?.propertyId, ctx?.tenantId], data);
      toast.success('Settings updated');
    },
    onError: () => toast.error('Failed to update settings'),
  });

  const onSubmit = async (values: DetailsValues) => {
    setSaveError(null);
    await saveDetails.mutateAsync(values);
  };

  const handleCancel = () => {
    reset({ companyName: tenant?.companyName ?? '', receptionHours: tenant?.receptionHours ?? '' });
    setSaveError(null);
    setEditing(false);
  };

  const setToggle = (key: keyof EnterpriseSettings, next: boolean) => {
    saveSettings.mutate({ [key]: next });
  };

  if (isError) {
    return (
      <div className="space-y-4 max-w-xl">
        <h1 className="text-2xl">Company Account</h1>
        <div role="alert" className="rounded-xl border border-destructive/40 bg-destructive/5 p-4">
          <p className="text-sm font-medium">Couldn&apos;t load company details</p>
          <p className="text-sm text-muted-foreground">Please try again later.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-xl">
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardContent className="space-y-4 pt-6">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-5 w-56" />
          </CardContent>
        </Card>
      </div>
    );
  }

  const settings = settingsQuery.data;
  const settingsBusy = saveSettings.isPending || settingsQuery.isLoading;

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center gap-3">
        <Building2 className="size-6 text-primary" />
        <h1 className="text-2xl">Company Account</h1>
      </div>

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="reception">Reception</TabsTrigger>
        </TabsList>

        {/* Details tab — unchanged from before */}
        <TabsContent value="details" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Company Details</CardTitle>
                {!editing && (
                  <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                    <Pencil className="size-4 mr-1" />
                    Edit
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editing ? (
                <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
                  <FieldGroup>
                    <Field>
                      <FieldLabel htmlFor="companyName">Company name</FieldLabel>
                      <Input
                        id="companyName"
                        type="text"
                        aria-invalid={!!errors.companyName}
                        {...register('companyName')}
                      />
                      {errors.companyName && (
                        <p className="text-sm text-destructive" role="alert">
                          {errors.companyName.message}
                        </p>
                      )}
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="receptionHours">Reception hours</FieldLabel>
                      <Input
                        id="receptionHours"
                        type="text"
                        placeholder="e.g. Mon–Fri 08:00–17:00"
                        {...register('receptionHours')}
                      />
                    </Field>
                    {saveError && (
                      <p className="text-sm text-destructive" role="alert">
                        {saveError}
                      </p>
                    )}
                    <div className="flex gap-2 pt-2">
                      <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Saving…' : 'Save'}
                      </Button>
                      <Button type="button" variant="outline" onClick={handleCancel}>
                        Cancel
                      </Button>
                    </div>
                  </FieldGroup>
                </form>
              ) : (
                <div className="space-y-4">
                  <DetailRow label="Company name" value={tenant?.companyName ?? null} />
                  <DetailRow label="Unit reference" value={tenant?.unitReference ?? null} />
                  <DetailRow label="Floor" value={tenant?.floor ?? null} />
                  <DetailRow label="Reception hours" value={tenant?.receptionHours ?? null} />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reception tab — ADR 008 toggles */}
        <TabsContent value="reception" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="size-4" />
                Reception &amp; notifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              {settingsQuery.isError ? (
                <div
                  role="alert"
                  className="rounded-xl border border-destructive/40 bg-destructive/5 p-4"
                >
                  <p className="text-sm font-medium">Couldn&apos;t load settings</p>
                  <p className="text-sm text-muted-foreground">Please try again later.</p>
                </div>
              ) : settingsQuery.isLoading || !settings ? (
                <div className="space-y-3">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : (
                <div className="divide-y divide-border/50">
                  <ToggleRow
                    title="Notify me when a visitor checks in"
                    description="Send a notification each time any of your visitors arrives at the gate."
                    checked={settings.notifyAdminOnArrival}
                    onChange={(v) => setToggle('notifyAdminOnArrival', v)}
                    disabled={settingsBusy}
                  />
                  <ToggleRow
                    title="Cc me on employee walk-ins"
                    description="When a guard sends a walk-in to a specific employee, I also see it in my approval queue."
                    checked={settings.notifyAdminOnWalkin}
                    onChange={(v) => setToggle('notifyAdminOnWalkin', v)}
                    disabled={settingsBusy}
                  />
                  <ToggleRow
                    title="Require my approval on employee invitations"
                    description="Invitations created by employees won't go out until I approve them. Off by default."
                    checked={settings.requireAdminApprovalForEmployeeInvites}
                    onChange={(v) => setToggle('requireAdminApprovalForEmployeeInvites', v)}
                    disabled={settingsBusy}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
